import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { SessionData } from '../../../shared/services/SessionsService';
import { User, StarTransaction } from '../../../types';

export interface StudentStar {
  reason: string;
  customReason?: string;
  timestamp: Date;
}

export interface ClassAttendanceData {
  sessionId: string;
  levelNumber: number;
  levelTitle: string;
  sessionDate: Date;
  lessonDateString: string; // YYYY-MM-DD format for star lookups
  wasPresent: boolean;
  attendees: {
    studentId: string;
    studentName: string;
  }[];
  starsEarned: StudentStar[]; // Stars earned during this specific session
}

export class ParentSessionService {
  /**
   * Get the recent class sessions for a specific squad with attendance data
   * @param squadId The squad ID
   * @param studentId The student ID to check attendance for
   * @param maxSessions Maximum number of sessions to return (default 5)
   */
  static async getRecentClassSessions(
    squadId: string,
    studentId: string,
    maxSessions: number = 5
  ): Promise<ClassAttendanceData[]> {
    try {
      // Query liveSessions for this squad, ordered by most recent first
      const sessionsRef = collection(db, 'liveSessions');

      let querySnapshot;

      try {
        // Try the indexed query first - get all sessions then we'll sort and limit
        const q = query(
          sessionsRef,
          where('squadId', '==', squadId),
          orderBy('startTime', 'asc')
        );
        querySnapshot = await getDocs(q);
      } catch (indexError) {
        console.warn('⚠️ Composite index not available yet. Falling back to unordered query:', indexError);

        // Fallback: query without ordering, then sort client-side
        const fallbackQuery = query(
          sessionsRef,
          where('squadId', '==', squadId)
        );
        querySnapshot = await getDocs(fallbackQuery);
      }

      const sessions: ClassAttendanceData[] = [];

      for (const sessionDoc of querySnapshot.docs) {
        const sessionData = sessionDoc.data() as SessionData;

        // Check if student was present
        const wasPresent = sessionData.attendanceData?.[studentId] === true;

        // Get list of students who attended
        const attendees: { studentId: string; studentName: string }[] = [];
        if (sessionData.attendanceData) {
          for (const [sid, present] of Object.entries(sessionData.attendanceData)) {
            if (present && sid !== studentId) {
              // Fetch student name
              try {
                const studentDoc = await getDoc(doc(db, 'users', sid));
                if (studentDoc.exists()) {
                  const studentData = studentDoc.data() as User;
                  const firstName = (studentData.displayName || 'Student').split(' ')[0];
                  attendees.push({
                    studentId: sid,
                    studentName: firstName
                  });
                }
              } catch (error) {
                console.warn(`Could not fetch student ${sid}:`, error);
              }
            }
          }
        }

        // Extract level number from levelId (format: "1", "2", etc.)
        const levelNumber = parseInt(sessionData.levelId) || 1;

        // Get lesson date in YYYY-MM-DD format
        const sessionDate = sessionData.startTime.toDate();
        const lessonDateString = this.formatDateToISO(sessionDate);

        // Fetch stars earned on this specific day
        const starsEarned = await this.getStarsForDate(studentId, lessonDateString, squadId);

        sessions.push({
          sessionId: sessionDoc.id,
          levelNumber,
          levelTitle: sessionData.levelTitle,
          sessionDate,
          lessonDateString,
          wasPresent,
          attendees,
          starsEarned
        });
      }

      // Sort by date (oldest first for carousel left-to-right) and limit
      sessions.sort((a, b) => a.sessionDate.getTime() - b.sessionDate.getTime());
      const limitedSessions = sessions.slice(-maxSessions); // Take the most recent N sessions
      return limitedSessions;
    } catch (error) {
      console.error('Error fetching recent class sessions:', error);
      return [];
    }
  }

  /**
   * Get the most recent session's data for a student
   */
  static async getMostRecentSession(
    squadId: string,
    studentId: string
  ): Promise<ClassAttendanceData | null> {
    const sessions = await this.getRecentClassSessions(squadId, studentId, 1);
    return sessions.length > 0 ? sessions[0] : null;
  }

  /**
   * Get a specific number of random classmates from the most recent session
   * (for generating the "teamed up with" text)
   */
  static getRandomClassmates(attendees: { studentId: string; studentName: string }[], count: number = 3): string[] {
    if (attendees.length === 0) return [];

    // Shuffle and take the first `count` items
    const shuffled = [...attendees].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, shuffled.length)).map(a => a.studentName);
  }

  /**
   * Format a Date object to ISO date string (YYYY-MM-DD)
   */
  private static formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get stars earned by a student on a specific lesson date
   */
  private static async getStarsForDate(
    studentId: string,
    lessonDate: string,
    squadId: string
  ): Promise<StudentStar[]> {
    try {
      // Query starTransactions for this student on this date
      const starsRef = collection(db, 'starTransactions');
      const q = query(
        starsRef,
        where('studentId', '==', studentId),
        where('lessonDate', '==', lessonDate),
        where('squadId', '==', squadId)
      );

      const querySnapshot = await getDocs(q);
      const stars: StudentStar[] = [];

      for (const starDoc of querySnapshot.docs) {
        const starData = starDoc.data() as StarTransaction;
        stars.push({
          reason: this.formatStarReason(starData.reason),
          customReason: starData.customReason,
          timestamp: starData.timestamp.toDate()
        });
      }

      return stars;
    } catch (error) {
      console.error('Error fetching stars for date:', error);
      return [];
    }
  }

  /**
   * Format star reason enum to human-readable text
   */
  private static formatStarReason(reason: string): string {
    const reasonMap: Record<string, string> = {
      'participation': 'Amazing Participation',
      'helpfulClassmate': 'Helpful Classmate',
      'creativity': 'Creative Thinking',
      'improvement': 'Great Improvement',
      'leadership': 'Leadership',
      'respect': 'Respectful Behavior',
      'effort': 'Outstanding Effort',
      'achievement': 'Achievement Unlocked',
      'other': 'Special Recognition'
    };

    return reasonMap[reason] || reason;
  }
}
